import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useShowNewPdpUI } from '@src/quince-site-pdp/src/templates/productPage/hooks/useShowNewPdpUI'

import Heart from '../../../../quince-site-common/src/organisms/appLayout/molecules/header/icons/heart.inline.svg'
import { IconButton } from '../icon-button'
import { Link } from '../link'
import ProductInfoPrice from '../productInfoPrice/ProductInfoPrice'
import { ProductTraditionalDiscountMessage } from '../productTraditionalDiscountMessage'
import ReviewStars from '../reviews/reviewStars'

import styles from './productInfo.module.scss'

export interface ProductInfoProps {
  productInfoHeaderClassName?: string
  productInfoHeaderTextClassName?: string
  productTitle: string
  formattedSalePrice?: string
  hideSalePrice?: boolean
  formattedTraditionalPrice?: string
  isWishListed?: boolean
  className?: string
  currency?: string
  avgRating?: number
  reviewCount?: number
  onWishListToggle?: (e: React.MouseEvent<HTMLElement>) => void
  hideWishListIcon?: boolean
  hideReviewSummary?: boolean
  onReviewSummaryClick?: (e: React.MouseEvent<HTMLElement>) => void
  savingPercentage?: number
  brandName?: string
  brandLink?: string
  traditionalDiscountMessage?: string
  formattedFestivalOfferPrice?: string
  showFullPDP?: boolean
  hideProductDetail?: boolean
  hideReviewCount?: boolean
  hideReviewLabel?: boolean
  productInfoReviewClassName?: string
  inStockMsgClass?: string
  showDiscount?: boolean
  pricePerServing?: number
  traditionalPricePerServing?: number
  isVitamin?: boolean
  hideSavings?: boolean
}

const ProductInfo = ({
  productInfoHeaderClassName,
  productInfoHeaderTextClassName,
  productTitle,
  formattedSalePrice,
  hideSalePrice = false,
  formattedTraditionalPrice,
  currency = '$',
  isWishListed = false,
  className,
  avgRating,
  reviewCount,
  onWishListToggle,
  hideWishListIcon,
  hideReviewSummary,
  onReviewSummaryClick,
  savingPercentage,
  brandName,
  brandLink,
  traditionalDiscountMessage,
  formattedFestivalOfferPrice,
  hideReviewCount,
  hideReviewLabel,
  productInfoReviewClassName,
  showDiscount,
  pricePerServing,
  traditionalPricePerServing,
  isVitamin,
  hideSavings,
}: ProductInfoProps) => {
  const { t } = useTranslation('common')
  const classes = classnames(styles.container, className)
  const STYLE_BLACK = 'black'
  const STYLE_WHITE = 'white'
  const STYLE_WIDTH = '20px'
  const STYLE_HEIGHT = '18px'
  const innerStyle = {
    // eslint-disable-next-line i18next/no-literal-string
    fill: isWishListed ? STYLE_BLACK : STYLE_WHITE,
    // eslint-disable-next-line i18next/no-literal-string
    width: STYLE_WIDTH,
    // eslint-disable-next-line i18next/no-literal-string
    height: STYLE_HEIGHT,
  }
  const showNewPdpUI = useShowNewPdpUI()

  return (
    <div className={classnames(classes, showNewPdpUI && styles.container_adjustedPadding)}>
      {brandName &&
        (brandLink ? (
          <Link href={brandLink} className={styles.container__brandName}>
            {brandName}
          </Link>
        ) : (
          <div className={styles.container__brandNameWithoutLink}>{brandName}</div>
        ))}
      <div
        className={classnames(
          styles.container__header,
          productInfoHeaderClassName,
          showNewPdpUI && styles.container__header_adjustedPadding,
        )}
      >
        <h1
          className={classnames(
            styles.container__header__text,
            productInfoHeaderTextClassName,
            showNewPdpUI && styles.container__header__text_adjustedPadding,
          )}
        >
          {productTitle}
        </h1>
        {!hideWishListIcon && (
          <IconButton
            className={classnames(
              styles.container__header__icon,
              showNewPdpUI && styles.container__header__icon_adjustedPadding,
            )}
            iconSvg={Heart}
            innerStyle={innerStyle}
            onClick={onWishListToggle}
            ariaLabel={t('mark_product_as_favourite')}
            dataTestId={isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON}
          />
        )}
      </div>
      {!hideReviewSummary && !!avgRating && Number(reviewCount) > 0 ? (
        <div
          className={classnames(
            styles.container__ratingAndReviews,
            showNewPdpUI && styles.container__ratingAndReviews_adjustedPadding,
          )}
          onClick={onReviewSummaryClick}
        >
          <ReviewStars
            totalStars={5}
            fillStars={avgRating}
            className={classnames(
              styles.container__ratingAndReviews__rating,
              showNewPdpUI && styles.container__ratingAndReviews__rating_adjustedPadding,
            )}
            classNameIcon={styles.reviewStarIcon}
          />
          {reviewCount && !hideReviewCount && (
            <p
              data-testid={TEST_LOCATORS.FIRST_FOLD_REVIEW_COUNT}
              className={classnames(
                styles.container__ratingAndReviews__reviews,
                productInfoReviewClassName,
                showNewPdpUI && styles.container__ratingAndReviews__reviews_adjustedPadding,
              )}
            >
              (
              <span>
                {reviewCount}
                {!hideReviewLabel && <>&nbsp;{reviewCount === 1 ? t('review') : t('reviews_v1')}</>}
              </span>
              )
            </p>
          )}
        </div>
      ) : null}
      {!hideSalePrice && !!formattedSalePrice && (
        <ProductInfoPrice
          formattedSalePrice={formattedSalePrice}
          formattedTraditionalPrice={formattedTraditionalPrice}
          currency={currency}
          savingPercentage={savingPercentage}
          formattedFestivalOfferPrice={formattedFestivalOfferPrice}
          showDiscount={showDiscount}
          pricePerServing={pricePerServing}
          traditionalPricePerServing={traditionalPricePerServing}
          isVitamin={isVitamin}
          hideSavings={hideSavings}
        />
      )}

      <ProductTraditionalDiscountMessage
        formattedTraditionalPrice={formattedTraditionalPrice}
        traditionalDiscountMessage={traditionalDiscountMessage}
      />
    </div>
  )
}

export default ProductInfo
